import { useEffect, useRef, useState } from "react";
import { useQueryClient } from "@tanstack/react-query";
import { toast } from "sonner";
import { useCredentialVaultRecordMutations } from "../../hooks/use-credential-vault-record-mutations";
import {
  vaultCandidatesQueryKey,
  type CredentialVaultScope,
} from "../../lib/credential-vault-scope";
import { invalidateCredentialVaultRecord } from "../../lib/credential-vault-invalidation";
import { useTRPC } from "@/trpc";
import type { Credential } from "@/features/credentials/types";
import {
  buildProviderCredentialSubstance,
  initialProviderFormValues,
  validateProviderForm,
} from "./provider-credential-form-values";
import {
  applyProviderReviewProposals,
  credentialWithReviewDefaults,
} from "./provider-credential-review-values";
import { useCredentialVaultReview } from "./use-credential-vault-review";
import {
  initialProfessionalValues,
  relatedProfessionalProfile,
  validateProfessionalDetails,
} from "./provider-professional-form-values";
import type { CredentialFormValues } from "../credential-edit-modal/credential-form-values";

export function useCredentialVaultEditor({
  credential,
  relatedCredentials = [],
  scope,
  reviewDocumentId,
  onClose,
  onSaved,
}: {
  credential: Credential;
  relatedCredentials?: Credential[];
  scope: CredentialVaultScope;
  reviewDocumentId?: string | null;
  onClose: () => void;
  onSaved?: () => void;
}) {
  const trpc = useTRPC();
  const queryClient = useQueryClient();
  const [baseline] = useState(() =>
    initialProfessionalValues({
      credential,
      credentials: relatedCredentials,
      values: initialProviderFormValues(credential),
    }),
  );
  const [values, setValues] = useState(baseline);
  const [formError, setFormError] = useState<string | null>(null);
  const [showDiscardWarning, setShowDiscardWarning] = useState(false);
  const [showRemoveWarning, setShowRemoveWarning] = useState(false);
  const [isFinishing, setIsFinishing] = useState(false);
  const blockedProposalKeys = useRef(new Set<string>());
  const appliedCandidateIds = useRef(new Set<string>());
  const review = useCredentialVaultReview({ scope, reviewDocumentId });
  const mutations = useCredentialVaultRecordMutations(scope);
  const isPending = mutations.isPending || isFinishing;
  const isEditing = credential.present && credential.credentialId !== null;
  const isDirty =
    review.hasChanges ||
    Object.keys(values).some((key) => values[key] !== baseline[key]);
  const hasPendingUpload =
    review.uploadState.isUploading || review.uploadState.pendingFileCount > 0;
  const isUploadingFiles = review.uploadState.isUploading;
  const isParsingFiles = review.isParsingFiles;
  const isFieldsDisabled = isPending || isUploadingFiles || isParsingFiles;
  const canSave =
    !isPending &&
    !hasPendingUpload &&
    !review.blocker &&
    (!isEditing || isDirty);

  useEffect(() => {
    const unapplied = review.keptItems.filter(
      (item) =>
        !item.candidate || !appliedCandidateIds.current.has(item.candidate.id),
    );
    if (unapplied.length === 0) {
      return;
    }
    const proposal = applyProviderReviewProposals({
      credential,
      values,
      blockedKeys: blockedProposalKeys.current,
      items: unapplied,
    });
    for (const key of proposal.proposedKeys) {
      blockedProposalKeys.current.add(key);
    }
    if (proposal.proposedKeys.size > 0) {
      setValues(proposal.values);
    }
    for (const item of unapplied) {
      if (
        item.candidate &&
        item.scanStatus === "clean" &&
        item.state !== "reading"
      ) {
        appliedCandidateIds.current.add(item.candidate.id);
      }
    }
  }, [review.keptItems, credential, values]);

  useEffect(() => {
    if (!isDirty && !isPending) {
      return;
    }
    function preventUnload(event: BeforeUnloadEvent) {
      event.preventDefault();
    }
    window.addEventListener("beforeunload", preventUnload);
    return () => window.removeEventListener("beforeunload", preventUnload);
  }, [isDirty, isPending]);

  function requestClose() {
    if (isPending || review.uploadState.isUploading) {
      return;
    }
    if (isDirty) {
      setShowDiscardWarning(true);
    } else {
      onClose();
    }
  }

  async function finish(message: string) {
    setIsFinishing(true);
    const invalidations = [
      invalidateCredentialVaultRecord({ queryClient, trpc, scope }),
    ];
    if (review.selectedIds.size > 0) {
      invalidations.push(
        queryClient.invalidateQueries({
          queryKey: vaultCandidatesQueryKey(trpc, scope),
        }),
      );
    }
    await Promise.all(invalidations);
    toast.success(message);
    onSaved?.();
    onClose();
  }

  async function save() {
    if (!canSave) {
      return;
    }
    const validationError =
      validateProviderForm({ credential, values }) ??
      validateProfessionalDetails({
        credentialType: credential.credentialType,
        values,
      });
    setFormError(validationError);
    if (validationError) {
      return;
    }
    try {
      const substance = buildProviderCredentialSubstance({
        credential: credentialWithReviewDefaults({
          credential,
          items: review.keptItems,
        }),
        values,
        baselineValues: baseline,
      });
      const relatedProfile = relatedProfessionalProfile({
        credentialType: credential.credentialType,
        values,
        baseline,
      });
      const relatedInput = relatedProfile
        ? { relatedProfessionalProfile: relatedProfile }
        : {};
      const reviewInput =
        review.reviewDocuments.length > 0
          ? { reviewDocuments: review.reviewDocuments }
          : {};
      if (isEditing && credential.credentialId) {
        // Removals preserve every other current document, including evidence
        // added after this editor opened. Omitting both lists preserves all.
        const removals =
          review.removedDocumentIds.size > 0
            ? { removeDocumentIds: [...review.removedDocumentIds] }
            : {};
        await mutations.update({
          credentialId: credential.credentialId,
          substance,
          ...relatedInput,
          ...reviewInput,
          ...removals,
        });
      } else {
        await mutations.create({
          credentialType: credential.credentialType,
          substance,
          ...relatedInput,
          ...reviewInput,
        });
      }
      await finish("Credential saved");
    } catch (error) {
      setIsFinishing(false);
      setFormError(
        error instanceof Error
          ? error.message
          : "Could not save this credential.",
      );
    }
  }

  async function removeCredential() {
    if (!credential.credentialId || isPending || hasPendingUpload) {
      return;
    }
    setFormError(null);
    try {
      await mutations.remove(credential.credentialId);
      await finish("Credential removed");
    } catch (error) {
      setIsFinishing(false);
      setShowRemoveWarning(false);
      setFormError(
        error instanceof Error
          ? error.message
          : "Could not remove this credential.",
      );
    }
  }

  function changeValue(key: string, value: string | boolean) {
    changeValues({ [key]: value });
  }

  function changeValues(patch: CredentialFormValues) {
    if (isFieldsDisabled) {
      return;
    }
    for (const key of Object.keys(patch)) {
      blockedProposalKeys.current.add(key);
    }
    setValues((previous) => ({ ...previous, ...patch }));
    setFormError(null);
  }

  return {
    baseline,
    values,
    formError,
    isPending,
    isUploadingFiles,
    isParsingFiles,
    isFieldsDisabled,
    isDirty,
    isEditing,
    canSave,
    hasPendingUpload,
    review,
    showDiscardWarning,
    setShowDiscardWarning,
    showRemoveWarning,
    setShowRemoveWarning,
    requestClose,
    save,
    removeCredential,
    changeValue,
    changeValues,
  };
}
